import React, { createContext, useState, ReactNode, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { refreshToken as refreshQuery } from '../queries/refreshtoken';
import { logout as logoutQuery } from '../queries/logout';
import { decodeToken } from '../utils/token';
import { usePersistentNavigate } from '../utils/navigation';
import { UserType } from '../schema';

export interface AuthContextType {
  accessToken: string | null;
  email: string | null;
  termsOfServiceAccepted?: boolean;
  completedRegistration?: boolean;
  userType: UserType | null | undefined;
  isLoading: boolean;
  login: (data: { accessToken: string; acceptedTermsOfService?: boolean; registrationComplete?: boolean; userType?: UserType; }) => void; // MODIFIED
  logout: () => void;
  setAccessToken: (token: string | null) => void;
  setTermsOfServiceAccepted: (accepted: boolean) => void;
  setCompletedRegistration: (completed: boolean) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [termsOfServiceAccepted, setTermsOfServiceAccepted] = useState<boolean | undefined>(undefined);
  const [completedRegistration, setCompletedRegistration] = useState<boolean | undefined>(undefined);
  const [userType, setUserType] = useState<UserType | null | undefined>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = usePersistentNavigate();

  const login = (data: { accessToken: string; acceptedTermsOfService?: boolean; registrationComplete?: boolean; userType?: UserType; }) => { // MODIFIED
    const decoded = decodeToken(data.accessToken);
    if (decoded) {
      setAccessToken(data.accessToken);
      setEmail(decoded.email);
      setTermsOfServiceAccepted(data.acceptedTermsOfService);
      setCompletedRegistration(data.registrationComplete);
      sessionStorage.setItem('cognitoUsername', decoded.sub);
      sessionStorage.setItem('termsOfServiceAccepted', data.acceptedTermsOfService ? 'true' : 'false');
      sessionStorage.setItem('completedRegistration', data.registrationComplete ? 'true' : 'false');

      if (data.userType) {
        setUserType(data.userType);
        sessionStorage.setItem('userType', data.userType.toString());
      }
    }
  };

  const logout = useCallback(async () => {
    try {
      await logoutQuery();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setAccessToken(null);
      setEmail(null);
      setTermsOfServiceAccepted(undefined);
      setCompletedRegistration(undefined);
      setUserType(null);
      sessionStorage.removeItem('cognitoUsername');
      sessionStorage.removeItem('termsOfServiceAccepted');
      sessionStorage.removeItem('completedRegistration');
      sessionStorage.removeItem('userType');
      sessionStorage.removeItem('lastSubmittedCaseResponse');
      sessionStorage.removeItem('caseConversation');
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    const attemptRefresh = async () => {
      const cognitoUsername = sessionStorage.getItem('cognitoUsername');
      const storedUserType = sessionStorage.getItem('userType');
      if (cognitoUsername) {
        try {
          const response = await refreshQuery(cognitoUsername);
          const newDecodedToken = decodeToken(response.accessToken);
          if (newDecodedToken) {
            setAccessToken(response.accessToken);
            setEmail(newDecodedToken.email);
            const acceptedTerms = sessionStorage.getItem('termsOfServiceAccepted');
            const registrationComplete = sessionStorage.getItem('completedRegistration');
            setTermsOfServiceAccepted(acceptedTerms === 'true' ? true : false);
            setCompletedRegistration(registrationComplete === 'true' ? true : false);

            if (storedUserType) {
              setUserType(parseInt(storedUserType) as UserType);
            }
          } else {
            throw new Error("Received invalid token from refresh endpoint");
          }
        } catch (error) {
          console.error("Session refresh failed:", error);
          await logout();
        }
      }

      setIsLoading(false);
    };

    if (accessToken === null) {
      attemptRefresh();
    }
  }, [logout]);

  const contextValue = {
    accessToken,
    email,
    termsOfServiceAccepted,
    completedRegistration,
    userType,
    isLoading,
    login,
    logout,
    setAccessToken,
    setTermsOfServiceAccepted,
    setCompletedRegistration,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};